import { useQuery } from '@tanstack/react-query'
import { Plus, Search, Server } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/ui/empty-state'
import { apiClient } from '@/lib/api-client'
import type { DnsRecord } from '@/types'

export function DnsPage() {
  const { data } = useQuery({
    queryKey: ['dns-records'],
    queryFn: () => apiClient.get<DnsRecord[]>('/dns/records'),
  })

  const records = data?.data || []

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">DNS Records</h1>
          <p className="text-muted-foreground">Manage DNS records for your domains</p>
        </div>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Record
        </Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search records..." className="pl-9" />
      </div>

      {records.length === 0 ? (
        <EmptyState
          icon={Server}
          title="No DNS records"
          description="Add domains first to manage DNS records."
          className="py-12"
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>DNS Records</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {records.map((record) => (
                <div key={record.id} className="flex items-center justify-between border-b pb-4 last:border-0">
                  <div className="flex items-center gap-4">
                    <Badge variant="outline">{record.type}</Badge>
                    <div>
                      <p className="font-medium">{record.name}</p>
                      <p className="text-sm text-muted-foreground">{record.value}</p>
                    </div>
                  </div>
                  <div className="text-sm text-muted-foreground">TTL: {record.ttl}s</div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
